import { collection, query, where, onSnapshot, doc, updateDoc, increment } from 'firebase/firestore';
import { useState, useEffect } from 'react';
import { db, auth } from '../services/firebase';
import WalletScreen from './WalletScreen';
import CallScreen from './CallScreen';
import RatingScreen from './RatingScreen';

const FILTERS = ["All", "Nursing", "Ausbildung", "Daily Life", "Multi-lang"];

const LANG_COLORS = {
  Malayalam: "#f97316",
  German: "#3b82f6",
  Hindi: "#a855f7",
  Tamil: "#10b981",
};

export default function HomeScreen({ userId }) {
  const [filter, setFilter] = useState("All");
  const [coins, setCoins] = useState(0);
  const [showWallet, setShowWallet] = useState(false);
  const [callingAgent, setCallingAgent] = useState(null);
  const [ratingSession, setRatingSession] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sessionEnded, setSessionEnded] = useState(false);

// Pre-warm token server
useEffect(() => {
  fetch('https://delrn-token-server.onrender.com/')
    .catch(() => {});
}, []);
  // Pre-request microphone permission
useEffect(() => {
  navigator.mediaDevices.getUserMedia({ audio: true })
    .then(stream => {
      stream.getTracks().forEach(track => track.stop());
      console.log('Microphone pre-authorized');
    })
    .catch(() => {});
}, []);
  useEffect(() => {
    const q = query(
      collection(db, 'agents'),
      where('isVerified', '==', true)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const agentList = snapshot.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          ratePerSecond: data.ratePerSecond?.toNumber ? data.ratePerSecond.toNumber() : data.ratePerSecond,
          totalSessions: data.totalSessions?.toNumber ? data.totalSessions.toNumber() : data.totalSessions,
          languages: Array.isArray(data.languages) ? data.languages : data.languages ? Object.values(data.languages) : [],
        };
      });
      setAgents(agentList);
      setLoading(false);
    });
    return unsubscribe;
  }, []);
useEffect(() => {
  if (!userId) return;
  const unsubscribe = onSnapshot(
    doc(db, 'users', userId),
    { includeMetadataChanges: true },
    (docSnap) => {
      if (docSnap.exists() && !docSnap.metadata.hasPendingWrites) {
        const newCoins = docSnap.data().coins || 0;
        setCoins(Math.max(0, newCoins));
      }
    }
  );
  return unsubscribe;
}, [userId]);
  
  if (showWallet) {
    return (
      <WalletScreen
        coins={coins}
        onBack={() => setShowWallet(false)}
        userId={userId}
      />
    );
  }

  if (callingAgent) {
    return (
      <CallScreen
        agent={callingAgent}
        coins={Math.max(0, coins)}
        onEnd={async (secs) => {
          console.log('onEnd called, secs:', secs, 'sessionEnded:', sessionEnded);
          if (sessionEnded) return;
          setSessionEnded(true);

          const coinsRanOut = secs === -1;
          const actualSecs = coinsRanOut ? Math.floor(coins / callingAgent.ratePerSecond) : secs;
          const used = coinsRanOut ? coins : Math.floor(secs * callingAgent.ratePerSecond);

          try {
            if (userId && used > 0 && !coinsRanOut) {
              await updateDoc(doc(db, 'users', userId), {
                coins: increment(-used)
              });
            }
            if (callingAgent.sessionId && used > 0) {
              await updateDoc(doc(db, 'sessions', callingAgent.sessionId), {
                status: 'completed',
                duration: actualSecs,
                coinsUsed: used,
                agentEarnings: Math.floor(used * 0.6),
              });
            }
          } catch (e) {
            console.error('Error:', e.message);
          }

          setSessionEnded(false);
          setRatingSession({ agent: callingAgent, duration: actualSecs });
          setCallingAgent(null);
        }}
        onEmergency={() => {
          alert('🛑 Emergency stop activated.');
          setCallingAgent(null);
        }}
      />
    );
  }
  if (ratingSession) {
    return (
      <RatingScreen
        agent={ratingSession.agent}
        duration={ratingSession.duration}
        onDone={() => setRatingSession(null)}
      />
    );
  }

  const filtered = agents.filter(a => {
    if (filter === "All") return true;
    if (filter === "Multi-lang") return a.languages && a.languages.length > 2;
    return a.specialty && a.specialty.includes(filter);
  });

return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.logoText}>
            <span style={styles.logoDe}>de</span>
            <span style={styles.logoLrn}>lrn</span>
          </div>
          <div style={styles.headerSub}>Speak German. Stay Anonymous.</div>
        </div>
        <div style={styles.headerRight}>
          <div style={styles.coinBadge} onClick={() => setShowWallet(true)}>
            <span style={styles.coinIcon}>🪙</span>
            <span style={styles.coinAmount}>{coins.toLocaleString()}</span>
            {coins < 100 && coins > 0 && <span style={styles.lowBadge}>Low</span>}
            {coins <= 0 && <span style={styles.lowBadge}>Add</span>}
          </div>
          <button style={styles.logoutBtn} onClick={() => {
            import('firebase/auth').then(({ signOut }) => signOut(auth));
          }}>⏻</button>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filters}>
        {FILTERS.map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              ...styles.filterBtn,
              ...(filter === f ? styles.filterActive : {})
            }}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Agent List */}
      <div style={styles.list}>
        {loading ? (
          <div style={styles.loading}>
            <div style={styles.loadingDot}></div>
            Finding your Sprachpartner...
          </div>
        ) : filtered.length === 0 ? (
          <div style={styles.empty}>
            <div style={styles.emptyIcon}>🌙</div>
            <div style={styles.emptyText}>No partners online</div>
            <div style={styles.emptySubText}>Check back in the evening</div>
          </div>
        ) : (
          filtered.map(agent => (
            <div key={agent.id} style={styles.card}>
              <div style={styles.cardTop}>
                <div style={{
                  ...styles.avatar,
                  boxShadow: agent.isOnline ? '0 0 0 2px #00d4a0' : '0 0 0 2px #ffffff20'
                }}>
                  {agent.avatar || '🎓'}
                  <div style={{
                    ...styles.onlineIndicator,
                    backgroundColor: agent.isOnline ? '#00d4a0' : '#ffffff30'
                  }}></div>
                </div>
                <div style={styles.cardInfo}>
                  <div style={styles.cardName}>{agent.codename}</div>
                  <div style={styles.cardSpec}>{agent.specialty}</div>
                  <div style={styles.langRow}>
                    {agent.languages && Object.values(agent.languages).map(l => (
                      <span key={l} style={{
                        ...styles.langBadge,
                        backgroundColor: (LANG_COLORS[l] || '#888') + '25',
                        color: LANG_COLORS[l] || '#888',
                        border: `1px solid ${(LANG_COLORS[l] || '#888')}40`,
                      }}>
                        {l.slice(0, 2).toUpperCase()}
                      </span>
                    ))}
                  </div>
                </div>
                <div style={styles.rateBox}>
                  <div style={styles.rateAmount}>🪙{agent.ratePerSecond}</div>
                  <div style={styles.rateLabel}>/sec</div>
                </div>
              </div>

              <div style={styles.statsRow}>
                <div style={styles.stat}>
                  <span style={styles.statVal}>⭐ {agent.rating}</span>
                  <span style={styles.statLbl}>Rating</span>
                </div>
                <div style={styles.statDivider}></div>
                <div style={styles.stat}>
                  <span style={styles.statVal}>{agent.totalSessions}</span>
                  <span style={styles.statLbl}>Sessions</span>
                </div>
                <div style={styles.statDivider}></div>
                <div style={styles.stat}>
                  <span style={styles.statVal}>{agent.location}</span>
                  <span style={styles.statLbl}>City</span>
                </div>
              </div>

              <button
                style={{
                  ...styles.callBtn,
                  ...(agent.isOnline ? {} : styles.callBtnDisabled)
                }}
                disabled={!agent.isOnline}
                onClick={async () => {
                  if (!agent.isOnline) return;
                  if (coins <= 0) {
                    alert('🪙 No coins! Please add coins first.');
                    setShowWallet(true);
                    return;
                  }
                  const { addDoc, collection, serverTimestamp } = await import('firebase/firestore');
                  const channelName = 'session_' + Date.now();
                  const sessionRef = await addDoc(collection(db, 'sessions'), {
                    agentId: agent.id,
                    customerId: userId || 'customer_test',
                    channelName: channelName,
                    status: 'calling',
                    startTime: serverTimestamp(),
                    ratePerSecond: agent.ratePerSecond,
                  });
                  setCallingAgent({ ...agent, channelName, sessionId: sessionRef.id });
                }}
              >
                {agent.isOnline ? '📞 Practice Now' : '⏸ In Session'}
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
const styles = {
  container: { backgroundColor: '#0a1628', minHeight: '100vh', fontFamily: "'Segoe UI', sans-serif" },
  header: { padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #ffffff10', background: 'linear-gradient(180deg, #0d2137, #0a1628)' },
  headerLeft: {},
  logoText: { fontSize: '24px', fontWeight: '900', lineHeight: 1 },
  logoDe: { color: '#ffffff' },
  logoLrn: { color: '#00d4a0' },
  headerSub: { color: '#ffffff40', fontSize: '11px', marginTop: '2px' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '8px' },
  coinBadge: { background: 'linear-gradient(135deg, #00d4a0, #00a87d)', borderRadius: '20px', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' },
  coinIcon: { fontSize: '14px' },
  coinAmount: { color: '#0a1628', fontWeight: '900', fontSize: '14px' },
  lowBadge: { backgroundColor: '#ff6b6b', color: '#ffffff', fontSize: '9px', borderRadius: '6px', padding: '1px 5px', fontWeight: '700' },
  logoutBtn: { backgroundColor: '#ffffff10', border: 'none', borderRadius: '10px', padding: '8px 10px', color: '#ffffff60', cursor: 'pointer', fontSize: '16px' },
  filters: { display: 'flex', gap: '8px', padding: '12px 16px', overflowX: 'auto', borderBottom: '1px solid #ffffff08' },
  filterBtn: { backgroundColor: '#0d2137', border: '1px solid #ffffff10', borderRadius: '20px', padding: '6px 14px', color: '#ffffff60', fontSize: '12px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' },
  filterActive: { backgroundColor: '#00d4a015', border: '1px solid #00d4a0', color: '#00d4a0' },
  list: { padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '600px', margin: '0 auto' },
  loading: { color: '#ffffff50', textAlign: 'center', padding: '60px 20px', fontSize: '15px' },
  loadingDot: { width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#00d4a0', margin: '0 auto 12px', animation: 'pulse 1s infinite' },
  empty: { textAlign: 'center', padding: '60px 20px' },
  emptyIcon: { fontSize: '48px', marginBottom: '12px' },
  emptyText: { color: '#ffffff', fontWeight: '800', fontSize: '18px', marginBottom: '8px' },
  emptySubText: { color: '#ffffff40', fontSize: '14px' },
  card: { backgroundColor: '#0d2137', borderRadius: '20px', padding: '16px', border: '1px solid #ffffff08' },
  cardTop: { display: 'flex', gap: '12px', marginBottom: '12px', alignItems: 'flex-start' },
  avatar: { width: '54px', height: '54px', borderRadius: '16px', backgroundColor: '#0a1628', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px', flexShrink: 0, position: 'relative' },
  onlineIndicator: { position: 'absolute', bottom: '2px', right: '2px', width: '10px', height: '10px', borderRadius: '50%', border: '2px solid #0d2137' },
  cardInfo: { flex: 1 },
  cardName: { color: '#ffffff', fontWeight: '800', fontSize: '15px', marginBottom: '2px' },
  cardSpec: { color: '#00d4a0', fontSize: '12px', fontWeight: '600', marginBottom: '6px' },
  langRow: { display: 'flex', gap: '4px' },
  langBadge: { borderRadius: '6px', padding: '2px 7px', fontSize: '10px', fontWeight: '700' },
  rateBox: { textAlign: 'center', flexShrink: 0 },
  rateAmount: { color: '#ffffff', fontWeight: '900', fontSize: '14px' },
  rateLabel: { color: '#ffffff40', fontSize: '10px' },
  statsRow: { display: 'flex', backgroundColor: '#0a1628', borderRadius: '12px', marginBottom: '12px', padding: '10px 0' },
  stat: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' },
  statDivider: { width: '1px', backgroundColor: '#ffffff10' },
  statVal: { color: '#ffffff', fontWeight: '700', fontSize: '13px' },
  statLbl: { color: '#ffffff40', fontSize: '10px' },
  callBtn: { width: '100%', background: 'linear-gradient(135deg, #00d4a0, #00a87d)', border: 'none', borderRadius: '14px', padding: '14px', color: '#0a1628', fontSize: '15px', fontWeight: '800', cursor: 'pointer' },
  callBtnDisabled: { background: '#ffffff10', color: '#ffffff30', cursor: 'not-allowed' },
};